import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef, useCallback } from 'react';
import { isEqual } from 'lodash-es';
import { Loader } from '../../Elements/Loader';
import { Tooltip } from '../../Feedback/Tooltip';
import { noop, valueAsSingleValue, valueAsMultiValue, onChangeAsSingle, onChangeAsMulti, getIsClickable, isValueSelected } from './helpers';
import { ChipItem, ChipsWrapper } from './styled';
// eslint-disable-next-line react/function-component-definition, @typescript-eslint/naming-convention
function _Chips(props, ref) {
    const { options, selectedValue, onChange, multi, inline, disabled: disabledProp, spacer, isClearable, isLoading, formatOptionLabel, dataTestId, nowrap, getOptionTooltip, } = props;
    // useCallback required here - useCallbackRef would capture stale selectedValue causing incorrect selections
    // eslint-disable-next-line no-restricted-syntax
    const updateSelectedValue = useCallback((value) => {
        if (multi) {
            const val = valueAsMultiValue(selectedValue);
            if (val.some((item) => isEqual(item, value))) {
                if (isClearable || val.length > 1) {
                    onChangeAsMulti(onChange)(val.filter((item) => !isEqual(item, value)));
                }
            }
            else {
                onChangeAsMulti(onChange)([...val, value]);
            }
        }
        else {
            const val = valueAsSingleValue(selectedValue);
            onChangeAsSingle(onChange)(isClearable && isEqual(val, value) ? null : value);
        }
    }, [isClearable, multi, onChange, selectedValue]);
    // useCallback required here - useCallbackRef would capture stale selectedValue causing incorrect selections
    // eslint-disable-next-line no-restricted-syntax
    const handleChange = useCallback((value) => () => {
        updateSelectedValue(value);
    }, [updateSelectedValue]);
    // useCallback required here - useCallbackRef would capture stale selectedValue causing incorrect selections
    // eslint-disable-next-line no-restricted-syntax
    const handleKeyPress = useCallback((value) => (event) => {
        if (event.key === 'Enter') {
            updateSelectedValue(value);
        }
    }, [updateSelectedValue]);
    // useCallback required here - useCallbackRef would capture stale selectedValue causing incorrect selections
    // eslint-disable-next-line no-restricted-syntax
    const getItemProps = useCallback(({ value, disabled: disabledItem }) => {
        const disabled = disabledProp || disabledItem;
        return {
            $multi: multi,
            checked: isValueSelected(selectedValue, value, multi),
            disabled,
            $isClickable: getIsClickable(selectedValue, value, multi, isClearable),
            onClick: disabled ? noop : handleChange(value),
            onKeyDown: disabled ? noop : handleKeyPress(value),
            tabIndex: 0,
            $labelTooltip: getOptionTooltip?.(value),
        };
    }, [disabledProp, handleChange, handleKeyPress, isClearable, multi, selectedValue, getOptionTooltip]);
    return (_jsx(ChipsWrapper, { "$inline": inline, "$spacer": spacer, "$isLoading": isLoading, tabIndex: 0, ref: ref, "data-testid": dataTestId, "$nowrap": nowrap, children: isLoading ? (_jsx(Loader, { dataTestId: 'chips-loader' })) : (options.map((option) => {
            const { $labelTooltip, ...itemProps } = getItemProps(option);
            return (_jsx(Tooltip, { placement: 'top', tooltip: $labelTooltip, children: _jsx(ChipItem, { ...itemProps, "data-testid": `${option.label.toLowerCase()}-${itemProps.checked}-chip`, children: formatOptionLabel ? formatOptionLabel(option) : option.label }) }, option.label));
        })) }));
}
export const Chips = forwardRef(_Chips);
